import * as React from 'react';
import {styled, Theme} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import * as R from 'ramda';
import {IWhiteList} from 'xss';
import {makeSxStyles} from '@practicegenius/pg-ui/src/base/Material/utils/sx-helpers';
import {
  addStyleAttrToWhiteListTags,
  SecureHtml,
} from '@practicegenius/pg-ui/src/base/SecureHtml';
import {Layout} from '../types';

const LayoutContext = React.createContext(Layout.public);
export const LayoutProvider = LayoutContext.Provider;
export const useLayout = () => React.useContext(LayoutContext);

const makeUseStyles = ({layout}: {layout: Layout}) =>
  makeSxStyles(theme => ({
    section: {
      marginBottom: layout === Layout.public ? theme.spacing(1) : 0,
      zIndex: 10,
      padding: 0,
      lineHeight: 1,
      maxWidth: layout === Layout.public ? 1024 : 'auto',
      width: '100%',
      backgroundColor: '#FFFFFF',
      overflow: 'hidden',

      [theme.breakpoints.up('sm')]: {
        borderRadius: theme.spacing(2),
        marginBottom: theme.spacing(3),

        ...(layout === Layout.public
          ? {
              marginTop: theme.spacing(1),
              width: '71%',
            }
          : {}),
      },
    },
  }));

const SxSection = styled('section')({});

export const Block: React.FCC = ({children, ...contentProps}) => {
  const layout = useLayout();
  const [styles] = makeUseStyles({layout})();

  return (
    <SxSection sx={styles.section} {...contentProps}>
      {children}
    </SxSection>
  );
};

// Different Blocks have different padding requirements, so the best thing I could think to do
// was split each padding declaration into its own function of theme, and compose them as needed
const contentTopPadding = (theme: Theme) => ({
  paddingTop: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingTop: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(6),
  },
});

const contentBottomPadding = (theme: Theme) => ({
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    paddingBottom: theme.spacing(4),
  },
  [theme.breakpoints.up('md')]: {
    paddingBottom: theme.spacing(6),
  },
});

const contentLeftPadding = (theme: Theme) => ({
  paddingLeft: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(6),
  },
  [theme.breakpoints.up('md')]: {
    paddingLeft: theme.spacing(9),
  },
});

const contentRightPadding = (theme: Theme) => ({
  paddingRight: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    paddingRight: theme.spacing(6),
  },
  [theme.breakpoints.up('md')]: {
    paddingRight: theme.spacing(9),
  },
});

// TODO The typings suck here: improve the situation
// - Why not just combine the CSSProperties?

/**
 * Combine multiple functions of Theme into a single style object
 */
const combineStyles = (fns: Function[]) =>
  // @ts-ignore This is basically impossible to type accurately
  R.pipe(R.prop('theme'), R.juxt(fns), R.reduce(R.mergeDeepLeft, {}));

export const BlockContent = styled(Box)(
  // @ts-ignore
  combineStyles([
    contentTopPadding,
    contentBottomPadding,
    contentLeftPadding,
    contentRightPadding,
  ])
);

export const ReviewContent = styled(Box)(
  // @ts-ignore
  combineStyles([
    contentLeftPadding,
    contentRightPadding,
    (theme: Theme) => ({
      paddingTop: theme.spacing(),
      paddingBottom: theme.spacing(),
    }),
  ])
);

const ReviewDividerWrapper = styled(Box)(
  // @ts-ignore
  combineStyles([
    contentLeftPadding,
    // Divider is full width for mobile view.
    (theme: Theme) => ({
      [theme.breakpoints.down('sm')]: {
        paddingLeft: 0,
      },
    }),
  ])
);

export const ReviewDivider = () => (
  <ReviewDividerWrapper>
    <Divider />
  </ReviewDividerWrapper>
);

export const BlockMedia: React.FC<
  React.ComponentProps<'img'> & {
    src: string;
    alt: string;
  }
> = props => <img {...props} />;

const BlockActionsContainer = styled(Box)(({theme}) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'space-between',
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(9),
    paddingRight: theme.spacing(9),
    flexDirection: 'row-reverse',
  },
}));

export const BlockActions: React.FCC = ({children}) => (
  <>
    <Divider />
    <BlockActionsContainer>{children}</BlockActionsContainer>
  </>
);

export const RichContent: React.FCC<{
  component?: keyof IWhiteList;
  className?: HTMLElement['className'];
  style?: Partial<HTMLElement['style']>;
}> = ({children, component, className, style}) => (
  <SecureHtml
    html={children}
    component={component}
    adaptWhitelist={addStyleAttrToWhiteListTags([
      'div',
      'span',
      'p',
      'a',
      'img',
    ])}
    className={className}
    style={style}
  />
);
